"use client"
import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { Layout, Menu, Button, Drawer, Badge, Avatar, Dropdown, Space, Spin , Typography} from "antd"
import {
  MenuOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  WalletOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  YoutubeOutlined,
} from "@ant-design/icons"
import { motion } from "framer-motion"
import { useAuth } from "../../contexts/AuthContext"
import { useCart } from "../../contexts/CartContext"
import { useWallet } from "../../contexts/WalletContext"
import { useCurrency } from "../../contexts/CurrencyContext"
import { useBlogAvailability } from "../../hooks/useBlogAvailability"
import { useData } from "../../contexts/DataContext"
import logo from "../../assets/logo.png"
// Header.css was orphaned — nothing imported it, so none of its rules ever
// shipped (including .desktop-menu and the 992px responsive breakpoints).
import "./Header.css"

const { Header: AntHeader } = Layout
const { Title, Text } = Typography

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, logout, loading} = useAuth()
  const { cartItems } = useCart()
  const { balance } = useWallet()
  const { formatMoney } = useCurrency()
  const hasBlogPosts = useBlogAvailability()
  const { navSections, fetchData } = useData()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // The nav is part of the global chrome, so it has to load its own data.
  // fetchData was only ever called by HomePage, which meant the networks
  // appeared on "/" and nowhere else. The context de-dupes on a 5-minute stale
  // window, so this costs nothing when the home page has already fetched.
  useEffect(() => {
    fetchData()
  }, [fetchData])
  const [scrolled, setScrolled] = useState(false)
  const [envText, setEnvText] = useState("")


  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Detect environment and set display text
  useEffect(() => {
    const url = window.location.href
    if (url.includes('staging')) {
      setEnvText('(Staging)')
    } else if (url.includes('localhost')) {
      setEnvText('(Dev)')
    } else {
      setEnvText('')
    }
  }, [])

  // Networks come from nav_sections, the admin-managed taxonomy, so the nav can
  // never advertise a network with nothing behind it and the client can rename
  // or re-order entries without a deploy. This list used to be hardcoded and had
  // drifted: it offered Taura and Bill Payments, which the taxonomy does not
  // carry, while omitting Telecel, which it does.
  const menuItems = [
    { key: "home", label: "Home", path: "/" },
    ...(navSections || []).map((section) => ({
      key: section.slug,
      label: section.name,
      path: `/products/${section.slug}`,
    })),
    // Blog is only surfaced once at least one post is published.
    ...(hasBlogPosts ? [{ key: "blog", label: "Blog", path: "/blog" }] : []),
  ]

  // User menu items
  const userMenuItems = [
    {
      key: "1",
      label: <Link to="/account">My Account</Link>,
    },
    {
      key: "2",
      label: <Link to="/account/email-preferences">Email preferences</Link>,
    },
    {
      key: "3",
      label: <span onClick={() => logout()}>Logout</span>,
    },
  ]

  const getCurrentMenuKey = () => {
    const path = location.pathname
    if (path === "/") return "home"

    const matchedItem = menuItems.find((item) => path.startsWith(item.path) && item.key !== "home")

    return matchedItem ? matchedItem.key : ""
  }

  return (
    <>
      <AntHeader
        className={`main-header ${scrolled ? "scrolled" : ""}`}
        style={{
          position: "sticky",
          backgroundColor: "white",
          top: 0,
          zIndex: 1000,
          width: "100%",
          padding: "0 20px",
          transition: "all 0.3s ease",
        }}
      >
        <div
          className="header-content"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            maxWidth: "1200px",
            margin: "0 auto",
            height: "64px",
          }}
        >
          {/* Logo group. The hamburger lives here — to the left of the logo —
              rather than over on the right with the cart/wallet/login, which is
              where it sat before. It is still governed by the same
              .mobile-menu-button CSS (hidden ≥992px), only its position moved,
              so desktop is unaffected: the button collapses out of flow there. */}
          <div className="header-left">
            <Button
              type="text"
              className="mobile-menu-button"
              aria-label="Open menu"
              icon={<MenuOutlined style={{ fontSize: 18 }} />}
              onClick={() => setMobileMenuOpen(true)}
            />
            <div className="logo">
              <Link to="/">
                <motion.img
                  src={logo}
                  style={{height: "30px", marginTop: "20px"}}
                  alt="Tanaka Life"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />

              </Link>
               {envText && <Text className="env-text" style={{color:'#c2410c'}}>{envText}</Text>}

            </div>
          </div>

          {/* Desktop network nav. These links lived only in the mobile drawer,
              so on a desktop screen the networks were unreachable — which is
              what the homepage tab slab was compensating for. */}
          <nav className="header-nav" aria-label="Networks">
            {menuItems
              .filter((item) => item.key !== "home" && item.key !== "blog")
              .map((item) => (
                <Link
                  key={item.key}
                  to={item.path}
                  className={
                    getCurrentMenuKey() === item.key
                      ? "header-nav-link header-nav-link-active"
                      : "header-nav-link"
                  }
                >
                  {item.label}
                </Link>
              ))}
          </nav>

          <div className="header-actions" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {hasBlogPosts && (
              <Link
                to="/blog"
                className="header-blog-link"
                style={{
                  color: location.pathname.startsWith("/blog") ? "#c2410c" : "#444",
                  fontWeight: 500,
                  fontSize: 14,
                  padding: "8px 12px",
                  marginRight: 4,
                  textDecoration: "none",
                }}
              >
                Blog
              </Link>
            )}
            <Badge count={cartItems.length} size="small" style={{marginRight:"1px"}}>
              <Button icon={<ShoppingCartOutlined />} style={{marginRight: "2px", backgroundColor:"whitesmoke"}} type="text" onClick={() => navigate("/checkout")} />
            </Badge>
            {isAuthenticated ? (
              <>
                <Button style={{marginRight:"0px"}} icon={<WalletOutlined />} type="text" onClick={() => navigate("/account")}>
                  {formatMoney(balance ?? 0)}
                </Button>

                <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                  <Space>
                    <Avatar size="small" icon={<UserOutlined />} />
                  </Space>
                </Dropdown>
              </>
            ) : (
              <Space>
                {
                  loading 
                  ? <Spin/> : 
                  <Button type="primary" onClick={() => navigate("/login")}>
                    Login
                  </Button>
                }
                
              </Space>
            )}
          </div>
        </div>
      </AntHeader>

      <Drawer title="Menu" placement="right" onClose={() => setMobileMenuOpen(false)} open={mobileMenuOpen} width={280}>
        <Menu
          mode="vertical"
          selectedKeys={[getCurrentMenuKey()]}
          items={menuItems.map((item) => ({
            key: item.key,
            label: (
              <Link to={item.path} onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>
            ),
          }))}
        />

        {isAuthenticated ? (
          <div className="mobile-user-actions">
           
          </div>
        ) : (
          <div className="mobile-auth-buttons">
            {
              loading ? <Spin/> :
              <Button
                type="primary"
                block
                onClick={() => {
                  navigate("/login")
                  setMobileMenuOpen(false)
                }}
                style={{ marginBottom: 10 }}
              >
                Login
              </Button>
            }
           
          </div>
        )}
      </Drawer>
    </>
  )
}

export default Header
